import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  b2bClassifyContacts, b2bGetClassifyStats,
  b2bGetStandardTitles, b2bCreateStandardTitle, b2bDeleteStandardTitle,
  b2bFilterContacts,
} from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

export default function TitleClassification() {
  const [tab, setTab]             = useState('report');
  const [stats, setStats]         = useState(null);
  const [distribution, setDist]   = useState([]);
  const [stdTitles, setStdTitles] = useState([]);
  const [newTitle, setNewTitle]   = useState('');
  const [addingTitle, setAddingTitle] = useState(false);
  const [scope, setScope]         = useState('unclassified');
  const [running, setRunning]     = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError]   = useState('');
  // Drill-down
  const [drillTitle, setDrillTitle]       = useState(null);
  const [drillContacts, setDrillContacts] = useState([]);
  const [drillLoading, setDrillLoading]   = useState(false);

  function loadStats() {
    b2bGetClassifyStats().then(r => { setStats(r.stats); setDist(r.distribution || []); }).catch(() => {});
  }
  function loadTitles() {
    b2bGetStandardTitles().then(r => setStdTitles(r.titles || [])).catch(() => {});
  }

  useEffect(() => { loadStats(); loadTitles(); }, []);

  async function handleRun() {
    setRunning(true); setRunResult(null); setRunError('');
    try {
      const r = await b2bClassifyContacts(scope);
      setRunResult(r);
      loadStats();
    } catch (e) { setRunError(e.message); }
    setRunning(false);
  }

  async function handleAddTitle(e) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAddingTitle(true);
    try {
      await b2bCreateStandardTitle(newTitle.trim());
      setNewTitle('');
      loadTitles();
    } catch (e) { alert(e.message); }
    setAddingTitle(false);
  }

  async function handleDeleteTitle(id, name) {
    if (!confirm(`Remove "${name}" from the standard titles list?`)) return;
    await b2bDeleteStandardTitle(id);
    loadTitles();
  }

  async function handleDrill(title) {
    if (drillTitle === title) { setDrillTitle(null); return; }
    setDrillTitle(title);
    setDrillLoading(true);
    try {
      const param = title === '(unclassified)' ? {} : { standard_titles: title };
      // For unclassified, we need contacts with no standard_title — use a large limit
      const r = await b2bFilterContacts({ ...param, limit: 500 });
      // If unclassified, filter client-side since API doesn't have a "no category" filter
      const contacts = title === '(unclassified)'
        ? (r.contacts || []).filter(c => !c.standard_title)
        : (r.contacts || []);
      setDrillContacts(contacts);
    } catch (e) { setDrillContacts([]); }
    setDrillLoading(false);
  }

  const pct = stats ? Math.round((stats.classified / Math.max(stats.total, 1)) * 100) : 0;

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-classify" />
      <main style={s.main}>
        <h1 style={s.title}>Title Classification</h1>

        {/* Stats boxes */}
        <div style={s.statsRow}>
          {[
            { label: 'Total Contacts',  value: stats?.total ?? '—',        color: NAVY },
            { label: 'Classified',      value: stats?.classified ?? '—',   color: '#059669' },
            { label: 'Unclassified',    value: stats?.unclassified ?? '—', color: ORANGE },
            { label: '% Classified',    value: stats ? pct + '%' : '—',    color: BLUE },
          ].map(({ label, value, color }) => (
            <div key={label} style={s.statBox}>
              <div style={{ color, fontSize: 28, fontWeight: 700 }}>{value}</div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          {[['report', 'Title Report'], ['classify', 'Run Classification'], ['titles', 'Manage Titles']].map(([key, label]) => (
            <button key={key} style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }}
              onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {/* ── REPORT TAB ── */}
        {tab === 'report' && (
          <div style={s.card}>
            <div style={s.cardHeader}>Distribution by Standard Title</div>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Standard Title', 'Contacts', 'Accounts', ''].map(h => <th key={h} style={s.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {distribution.length === 0 && (
                  <tr><td colSpan={4} style={s.empty}>No data yet</td></tr>
                )}
                {distribution.map(row => (
                  <>
                    <tr key={row.standard_title} style={{ ...s.tr, cursor: 'pointer', background: drillTitle === row.standard_title ? 'rgba(1,118,206,0.04)' : undefined }}
                      onClick={() => handleDrill(row.standard_title)}>
                      <td style={{ ...s.td, fontWeight: 500 }}>
                        {row.standard_title === '(unclassified)'
                          ? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{row.standard_title}</span>
                          : <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, fontSize: 12, fontWeight: 600, padding: '2px 8px' }}>{row.standard_title}</span>
                        }
                      </td>
                      <td style={{ ...s.td, fontWeight: 600, color: NAVY }}>{row.contact_count}</td>
                      <td style={{ ...s.td, color: '#64748b' }}>{row.account_count}</td>
                      <td style={{ ...s.td, color: BLUE, fontSize: 12 }}>
                        {drillTitle === row.standard_title ? '▲ Hide' : '▼ Show contacts'}
                      </td>
                    </tr>
                    {drillTitle === row.standard_title && (
                      <tr key={row.standard_title + '-drill'}>
                        <td colSpan={4} style={{ padding: 0, background: '#f8fafc' }}>
                          {drillLoading ? (
                            <div style={{ padding: '16px 24px', color: '#64748b', fontSize: 13 }}>Loading…</div>
                          ) : drillContacts.length === 0 ? (
                            <div style={{ padding: '16px 24px', color: '#94a3b8', fontSize: 13 }}>No contacts found</div>
                          ) : (
                            <table style={{ ...s.table, margin: 0 }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9' }}>
                                  {['Name', 'Raw Title', 'Company', 'Email'].map(h => (
                                    <th key={h} style={{ ...s.th, fontSize: 10, background: '#f1f5f9' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {drillContacts.map(c => (
                                  <tr key={c.contact_id} style={s.tr}>
                                    <td style={s.td}>
                                      <Link to={`/admin/b2b/contacts/${c.contact_id}`}
                                        style={{ color: NAVY, fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
                                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                                      </Link>
                                    </td>
                                    <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || '—'}</td>
                                    <td style={{ ...s.td, fontSize: 13, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || '—'}</td>
                                    <td style={{ ...s.td, fontSize: 12, color: BLUE }}>{c.email || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── CLASSIFY TAB ── */}
        {tab === 'classify' && (
          <div style={s.card}>
            <div style={s.cardHeader}>Apply Classification</div>
            <div style={{ padding: 24 }}>
              <div style={s.optionLabel}>Scope</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                <label style={s.radio}>
                  <input type="radio" value="unclassified" checked={scope === 'unclassified'}
                    onChange={() => setScope('unclassified')} style={{ marginRight: 10, marginTop: 2 }} />
                  <div>
                    <div style={{ color: NAVY, fontWeight: 600, fontSize: 13 }}>Unclassified contacts only</div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>Only contacts without a standard title. Uses cache — fast.</div>
                  </div>
                </label>
                <label style={s.radio}>
                  <input type="radio" value="all" checked={scope === 'all'}
                    onChange={() => setScope('all')} style={{ marginRight: 10, marginTop: 2 }} />
                  <div>
                    <div style={{ color: NAVY, fontWeight: 600, fontSize: 13 }}>All contacts</div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>Clears cache and re-runs AI on every contact. Use after changing the titles list.</div>
                  </div>
                </label>
              </div>
              <button style={{ ...s.runBtn, opacity: running ? 0.7 : 1 }} onClick={handleRun} disabled={running}>
                {running ? '⏳ Classifying…' : '✦ Run Classification'}
              </button>
              {runError && <div style={s.errorBox}>{runError}</div>}
              {runResult && (
                <div style={s.successBox}>
                  <strong>Done.</strong> {runResult.titles_classified} unique title{runResult.titles_classified !== 1 ? 's' : ''} processed · {runResult.updated} contact{runResult.updated !== 1 ? 's' : ''} updated
                </div>
              )}
              <div style={{ marginTop: 24, color: '#64748b', fontSize: 13, lineHeight: 1.7, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                <strong style={{ color: NAVY }}>How it works:</strong> Collects distinct raw titles → checks DB cache → sends uncached titles to Claude Haiku in one batch → updates <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>standard_title</code> on all matching contacts.
              </div>
            </div>
          </div>
        )}

        {/* ── MANAGE TITLES TAB ── */}
        {tab === 'titles' && (
          <div style={s.card}>
            <div style={s.cardHeader}>Standard Title Categories</div>
            <div style={{ padding: 20 }}>
              <form onSubmit={handleAddTitle} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <input style={{ ...s.inp, flex: 1 }} placeholder="New standard title…"
                  value={newTitle} onChange={e => setNewTitle(e.target.value)} />
                <button type="submit" style={s.addBtn} disabled={addingTitle || !newTitle.trim()}>
                  {addingTitle ? 'Adding…' : '+ Add'}
                </button>
              </form>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {stdTitles.map(t => (
                  <div key={t.title_id} style={s.titleRow}>
                    <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, fontSize: 12, fontWeight: 600, padding: '3px 10px' }}>{t.title_name}</span>
                    <button style={s.deleteBtn} onClick={() => handleDeleteTitle(t.title_id, t.title_name)}>✕</button>
                  </div>
                ))}
                {stdTitles.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>No titles yet</div>}
              </div>
              <div style={{ marginTop: 16, color: '#94a3b8', fontSize: 12 }}>
                Changes take effect on the next classification run. Delete a title only if it's no longer needed — existing contacts will keep their current value.
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page:       { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:       { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  title:      { color: NAVY, fontSize: 24, fontWeight: 700, margin: '0 0 20px' },
  statsRow:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statBox:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  statLabel:  { color: '#64748b', fontSize: 12, fontWeight: 500, marginTop: 4 },
  tabs:       { display: 'flex', gap: 4, marginBottom: 20 },
  tab:        { background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '8px 18px', borderRadius: 8 },
  tabActive:  { background: '#fff', color: NAVY, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  card:       { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardHeader: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9', color: NAVY, fontSize: 13, fontWeight: 600 },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:         { borderBottom: '1px solid #f1f5f9' },
  td:         { color: '#374151', fontSize: 13, padding: '11px 16px', verticalAlign: 'middle' },
  empty:      { color: '#94a3b8', fontSize: 13, padding: 32, textAlign: 'center' },
  optionLabel:{ color: '#374151', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  radio:      { display: 'flex', alignItems: 'flex-start', padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fafafa' },
  runBtn:     { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 12 },
  errorBox:   { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginTop: 8 },
  successBox: { background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)', borderRadius: 8, color: '#065f46', fontSize: 13, padding: '10px 14px', marginTop: 8 },
  inp:        { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' },
  addBtn:     { background: NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  titleRow:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 6, background: '#f8fafc' },
  deleteBtn:  { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 4 },
};
